import CheckCircleIcon from "@heroicons/react/solid/CheckCircleIcon"
import InformationCircleIcon from "@heroicons/react/solid/InformationCircleIcon"
import { FormEvent, useEffect, useRef } from "react"
import ProjectInfoCard from "~/components/ProjectInfoCard"
import RecruiterHeader from "~/components/RecruiterHeader"
import Variant from "~/const/variant"
import { Data } from "~/types/dataTypes"
import { IProjectForm, IProjectIntro } from "~/types/recruiter"
import { useRecruiterForm } from "~/util/useRecruiterForm"
import AuthenticityTokenInput from "../AuthenticityTokenInput"
import Button, { ButtonSize } from "../Button"
import Card from "../Card"
import Icon from "../Icon"
import { Markdown } from "../Markdown"
import { RecruiterModal } from "../RecruiterModal"
import RedcapField from "../RedcapField"
import "./index.scss"
import { Question } from "./Question"

const baseProjectPath = window.location.pathname.match(/\/recruiter\/[^/]+/)![0]

type IRecruiterScreenFormProps = {
  intro: IProjectIntro
  form: IProjectForm
  projectPrefix: string
}

export default function RecruiterScreenForm({
  intro,
  form,
  projectPrefix
}: IRecruiterScreenFormProps) {
  const { submit, status, fieldErrors, reset, data, setData } =
    useRecruiterForm(projectPrefix)

  const fieldRefs = useRef<Record<string, HTMLLIElement | null>>({}).current

  const updateFields = (newData: Data) => {
    setData((prevData) => ({ ...prevData, ...newData }))
  }

  useEffect(() => {
    const firstInvalidField = form.questions.find(
      ({ fieldName }) => fieldErrors[fieldName]
    )
    if (!firstInvalidField) return

    const element = fieldRefs[firstInvalidField.fieldName]
    if (!element) return

    element.scrollIntoView({ behavior: "smooth" })
  }, [fieldErrors])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    submit()
  }

  const renderModal = () => {
    let modalConfig = null
    if (status === "submitted") modalConfig = form.successModal
    if (status === "ineligible") modalConfig = form.ineligibleModal
    if (!modalConfig) return

    return (
      <RecruiterModal className="space-y-6">
        <div className="flex gap-2.5 items-start">
          {status === "submitted" ? (
            <CheckCircleIcon className="w-6 my-0.5 shrink-0 fill-success-500" />
          ) : (
            <InformationCircleIcon className="w-6 my-0.5 shrink-0 fill-warning-500" />
          )}
          <h2 className="text-2xl leading-7 text-primary-900 flex-1">
            {modalConfig.title}
          </h2>
        </div>
        <Markdown className="RecruiterScreenForm__markdown">
          {modalConfig.content}
        </Markdown>
        <div className="flex gap-4">
          <Button
            variant="secondary"
            size={ButtonSize.ExtraLarge}
            className="flex-1"
            onClick={reset}
          >
            Luk
          </Button>
          <form action={`${baseProjectPath}/sessions`} method="post">
            <AuthenticityTokenInput />
            <input type="hidden" name="_method" value="delete" />
            <Button type="submit" variant="basic" size={ButtonSize.ExtraLarge}>
              Log ud
            </Button>
          </form>
        </div>
      </RecruiterModal>
    )
  }

  return (
    <div className="min-h-full flex flex-col bg-primary-100">
      <RecruiterHeader buttons={["logout"]} />
      <main className="flex-1 flex items-start justify-center px-6 py-8 md:px-12 md:py-12">
        <div className="w-full max-w-5xl flex flex-col md:flex-row gap-6 md:gap-8 items-start">
          <div className="w-full md:w-1/2">
            <ProjectInfoCard {...intro} />
          </div>
          <form onSubmit={handleSubmit} className="w-full md:w-1/2">
            <Card dataTestId="survey-single-page-form">
              <p className="text-primary-900">{form.title}</p>
              <ul className="space-y-8 my-10">
                {form.questions.map((question) => (
                  <li
                    key={question.fieldName}
                    ref={(ref) => {
                      fieldRefs[question.fieldName] = ref
                    }}
                    className="space-y-4"
                  >
                    <Question question={question} />
                    <RedcapField
                      value={data[question.fieldName]}
                      question={question}
                      onChange={(value) =>
                        updateFields({ [question.fieldName]: value })
                      }
                    />
                    {fieldErrors[question.fieldName] && (
                      <p className="text-danger-500 mt-2">
                        {fieldErrors[question.fieldName]}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
              {status === "error" && (
                <p className="text-danger-500 mb-2">
                  Der opstod en fejl, prøv venligst igen
                </p>
              )}
              <Button
                variant={Variant.Primary}
                type="submit"
                className={`p-4 w-full rounded-xl flex justify-between ${status === "loading" ? "bg-primary-50" : ""}`}
                disabled={status === "loading"}
              >
                <p className="font-black text-white">
                  {form.submitButton.label}
                </p>
                <Icon name={form.submitButton.icon} />
              </Button>
            </Card>
          </form>
        </div>
      </main>
      {renderModal()}
    </div>
  )
}
